import { ref } from "vue";
import axios from "axios";
import { formattedToUserTimeZone } from "@/features/shared/utils/utils.js";
import { useGlobalStore } from "@/store/global.js";

export function useIntegrationComposable() {
    const platformsStores = ref([]);
    const platformsOrders = ref([]);
    const admStoresDetails = ref([]);
    const availableSources = ref([]);
    const errorsRow = ref([]);
    const totalPage = ref(0);
    const storesManagementData = ref(null);

    const isLoading = ref(false);
    const hasError = ref(null);
    
    const globalStore = useGlobalStore();

    function processOrderData(response) {
        if (response && response.responseCode === 0 && Array.isArray(response.ordersTotal)) {
            if (response.ordersTotal.length === 0) {
                // Si no hay datos, limpiar el gráfico
                platformsOrders.value = [];
                return;
            }
            
            // Group orders by source
            const ordersBySource = response.ordersTotal.reduce((acc, order) => {
                if (!acc[order.source]) {
                    acc[order.source] = {};
                }
                if (!acc[order.source][order.orderStatus]) {
                    acc[order.source][order.orderStatus] = 0;
                }
                acc[order.source][order.orderStatus] += order.quantity;
                return acc;
            }, {});
            
            // Convert to chart format - Order matches color assignments
            platformsOrders.value = Object.keys(ordersBySource).map(source => {
                const chartData = [
                    { value: ordersBySource[source]['Aceptado'] || 0, name: 'Aceptados' },     // Verde
                    { value: ordersBySource[source]['Rechazado'] || 0, name: 'Rechazados' },   // Rojo
                    { value: ordersBySource[source]['Pendiente'] || 0, name: 'Pendientes' },    // Amarillo
                    { value: ordersBySource[source]['Recibido'] || 0, name: 'Recibidos' },     // Naranja
                    { value: ordersBySource[source]['Despachado'] || 0, name: 'Despachados' }, // Azul
                    { value: ordersBySource[source]['Cancelado'] || 0, name: 'Cancelados' }     // Púrpura - FIX: 'Cancelado' vs 'Cancelados'
                ];
                return {
                    name: source,
                    chartData
                };
            });
        } else {
            // Si la respuesta no es válida o hay error, limpiar el gráfico
            platformsOrders.value = [];
        }
    }

    function processStoreStatus(response) {
        if (response && response.responseCode === 0 && Array.isArray(response.stores)) {
        platformsStores.value = response.stores.map(store => ({
            name: store.source,
            chartData: [
            { value: store.opennedQty, name: 'Abiertas' },
            { value: store.closedQty, name: 'Cerradas' }
            ]
        }));
        }
    }

    function processErrorLogs(logs = []) {
        errorsRow.value = logs.map((log) => {
        const formattedDate = formattedToUserTimeZone(log.creationTimestamp)
        const [date, hour] = formattedDate.split(" ");
        return {
            date,
            hour,
            shopName: log.shopName,
            bistroShopCode: log.bistroShopCode,
            errorMessage: log.auditMessage,
            orderSourceCode: log.orderSourceCode
        };
        });
    }

    async function fetchOrdersData(shopCodes) {
        globalStore.initLoading();
        try {
            const ordersRes = await axios.get("/api/dashboardV2/getIntegrationsOrders", {
                params: { shopCodes }
            });

            processOrderData(ordersRes.data);

        } catch (error) {
            hasError.value = error;
            console.error("Error al obtener datos de pedidos:", error);
        } finally {
            globalStore.finishLoading();
        }
    }

    async function fetchOrdersTotals(shopCodes) {
        globalStore.initLoading();
        try {
            // Convert shopCodes array to comma-separated string for the API
            const shopCodesParam = Array.isArray(shopCodes) ? shopCodes.join(',') : shopCodes;
            
            const ordersRes = await axios.get("/api/dashboardV2/getOrdersTotals", {
                params: { ShopCodes: shopCodesParam }
            });

            processOrderData(ordersRes.data);
        } catch (error) {
            hasError.value = error;
            console.error("Error al obtener totales de pedidos:", error);
        } finally {
            globalStore.finishLoading();
        }
    }

    async function fetchOrdersDetails(params = {}) {
        globalStore.initLoading();
        try {
            // Convert shopCodes array to comma-separated string if needed
            const shopCodesParam = Array.isArray(params.shopCodes) ? params.shopCodes.join(',') : params.shopCodes;
            
            const queryParams = {
                ShopCodes: shopCodesParam,
                source: params.source,
                status: params.status,
                orderFilter: params.searchQuery || params.orderFilter,
                pageNumber: params.pageNumber,
                pageSize: params.pageSize
            };

            // Remove undefined/null parameters
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] === undefined || queryParams[key] === null) {
                    delete queryParams[key];
                }
            });

            const ordersRes = await axios.get("/api/dashboardV2/getOrdersDetails", {
                params: queryParams
            });

            return ordersRes.data;

        } catch (error) {
            hasError.value = error;
            console.error("Error al obtener detalles de pedidos:", error);
            throw error;
        } finally {
            globalStore.finishLoading();
        }
    }

    async function fetchStoreStatusData(shopCodes) {
        globalStore.initLoading();
        try {
            const statusRes = await axios.get("/api/dashboardV2/getIntegrationsStatus", {
                params: { shopCodes }
            });

            processStoreStatus(statusRes.data);

        } catch (error) {
            hasError.value = error;
            console.error("Error al obtener datos de estado de tiendas:", error);
        } finally {
            globalStore.finishLoading();
        }
    }

    async function fetchIntegrationErrors(shopCodes, currentPage, countPerPage) {
        globalStore.initLoading();
        try {
            const { data } = await axios.get("/api/dashboardV2/getIntegrationsErrors", {
            params: { shopCodes, currentPage, countPerPage }
            });

            if (data?.errorSyncAuditLogList) {
                processErrorLogs(data.errorSyncAuditLogList);
                totalPage.value = data.totalCount;
            }
        } catch (error) {
            hasError.value = error;
            console.error("Error al obtener errores de integración:", error);
        } finally {
            globalStore.finishLoading();
        }
    }

    async function fetchStoreDetails(shopCodes, filters) { 
        globalStore.initLoading();
        try {
            const { data } = await axios.get("/api/dashboardV2/getIntegrationStoresDetails", {
                params: { shopCodes, filters }
            });
            if (data?.stores) {
                admStoresDetails.value = data.stores;
                availableSources.value = data.stores.length > 0 ? data.stores[0].integrations : [];
            }
        } catch (error) {
            hasError.value = error;
            console.error("Error al obtener datos de integraciones:", error);    
        } finally {
            globalStore.finishLoading();
        }
    }

    /**
     * BPM-26: `withLoading: false` para quien ya muestra su propia carga en
     * pantalla. El loading global no lleva contador — `finishLoading` pone el
     * progreso en 100 y limpia el interval compartido — así que una llamada de
     * fondo le apagaría el spinner a la operación que está en curso.
     */
    async function getStoreIntegration(shopCode, { withLoading = true } = {}) {
        if (withLoading) globalStore.initLoading();
        try {
            const { data } = await axios.get("/api/dashboardV2/getStoreIntegration", {
                params: { shopCode }
            });
            return data;
        } catch (error) {
            hasError.value = error;
            console.error("Error al obtener integración de tienda:", error);
            if (error.response && error.response.data) {
                return error.response.data;
            }
            return error;
        } finally {
            if (withLoading) globalStore.finishLoading();
        }
    }

    async function updateStoreIntegration(shopCode, storeIntegrations) {
        globalStore.initLoading();
        try {
            const { data } = await axios.put('/api/dashboardV2/updateStoreIntegration', {
                shopCode,
                StoreIntegrations: storeIntegrations
            });
            return data;
        } catch (error) {
            hasError.value = error;
            console.error("Error al actualizar integración de tienda:", error);
            if (error.response && error.response.data) {
                return error.response.data;
            }
            return error;
        } finally {
            globalStore.finishLoading();
        }
    }

    async function updateStoreIntegrationStatus(body) {
        globalStore.initLoading();
        try {
            const  { data }  = await axios.put(`/api/dashboardV2/updateIntegrationStoreStatus`, body);
            return data;
        } catch (error) {
            if (error.response && error.response.data) {
                return error.response.data;
            } else {
                return error;
            }
        } finally {
            globalStore.finishLoading();
        }
    }

    async function updateBulkStoreIntegrationStatus(action = "OPEN", shopCodes = []) {
        const storesToUpdate = admStoresDetails.value
            .filter(store => shopCodes.includes(store.shopCode))
            .map(store => {
                const filteredIntegrations = store.integrations
                    .filter(integration => {
                        if (integration.status === "NOTINTEGRATED") return false;
                        if (action === "OPEN" && integration.status === "CLOSED") return true;
                        if (action === "CLOSED" && integration.status === "OPEN") return true;
                        return false;
                    })
                    .map(integration => ({
                        source: integration.source,
                        oldStatus: integration.status,
                        newStatus: action
                    }));
                if (filteredIntegrations.length === 0) return null;
                return {
                    shopCode: store.shopCode,
                    integrations: filteredIntegrations
                };
            })
            .filter(Boolean);
        
        const body = { stores: storesToUpdate };
        
        globalStore.initLoading();
        try {
            const { data } = await axios.put('/api/dashboardV2/updateBulkStoreIntegrationStatus', body);
            return data;
        } catch (error) {
            if (error.response && error.response.data) {
                return error.response.data;
            } else {
                return error;
            }
        } finally {
            globalStore.finishLoading();
        }
    }

    async function fetchStoresManagement(shopCodes, menuStatusFilter = null, isOperativeFilter = null, storeStatusFilter = null, sourceFilter = null) {
        globalStore.initLoading();
        try {
            const normalizedShopCodes = Array.isArray(shopCodes)
                ? [...new Set(shopCodes.filter(Boolean).map((x) => String(x).trim()).filter(Boolean))]
                : shopCodes
                    ? [...new Set(String(shopCodes)
                        .split(',')
                        .map((x) => x.trim())
                        .filter(Boolean))]
                    : [];


            if (normalizedShopCodes.length === 0) {
                console.error('[fetchStoresManagement] ShopCodes parameter is required');
                storesManagementData.value = null;
                return {
                    responseCode: 1,
                    responseMessage: 'ShopCodes parameter is required'
                };
            }

            const queryParams = {
                shopCodes: normalizedShopCodes.join(','),
                menuStatusFilter: menuStatusFilter,
                isOperativeFilter: isOperativeFilter,
                storeStatusFilter: storeStatusFilter,
                sourceFilter: sourceFilter
            };

            Object.keys(queryParams).forEach((key) => {
                const value = queryParams[key];
                // Solo eliminar si es undefined, null o string vacío, pero NO si es 0 o false
                if (value === undefined || value === null || value === '') {
                    delete queryParams[key];
                }
            });

            const queryStringParts = [];
            if (queryParams.menuStatusFilter !== undefined) queryStringParts.push(`MenuStatusFilter=${queryParams.menuStatusFilter}`);
            if (queryParams.shopCodes !== undefined) queryStringParts.push(`ShopCodes=${queryParams.shopCodes}`);
            if (queryParams.isOperativeFilter !== undefined) queryStringParts.push(`IsOperativeFilter=${queryParams.isOperativeFilter}`);
            if (queryParams.storeStatusFilter !== undefined) queryStringParts.push(`storeStatusFilter=${queryParams.storeStatusFilter}`);
            if (queryParams.sourceFilter !== undefined) queryStringParts.push(`SourceFilter=${queryParams.sourceFilter}`);
            const queryString = queryStringParts.join('&');

            const { data } = await axios.get(`/api/dashboardV2/getStoresManagement?${queryString}`);

            if (data && data.responseCode === 0) {
                storesManagementData.value = data;
            } else {
                console.error('Error fetching stores management data:', data?.responseMessage);
                storesManagementData.value = null;
            }
            
            return data;
        } catch (error) {
            console.error('Error fetching stores management data:', error);
            storesManagementData.value = null;
            if (error.response && error.response.data) {
                return error.response.data;
            } else {
                return error;
            }
        } finally {
            globalStore.finishLoading();
        }
    }

    return {
        platformsStores,
        platformsOrders,
        errorsRow,
        totalPage,
        fetchOrdersData,
        fetchOrdersTotals,
        fetchOrdersDetails,
        fetchStoreStatusData,
        fetchIntegrationErrors,
        processOrderData,
        processStoreStatus,
        processErrorLogs,
        isLoading,
        hasError,
        fetchStoreDetails,
        getStoreIntegration,
        updateStoreIntegration,
        admStoresDetails,
        availableSources,
        updateStoreIntegrationStatus,
        updateBulkStoreIntegrationStatus,
        fetchStoresManagement,
        storesManagementData
    };
}
